import { apiURL } from './constants';
import axios from 'axios';

/**
 * Shipment Pieces & Scan Flows API client.
 *
 * Companion docs:
 *  - SHIPMENT_PIECE_API_DOCUMENTATION.md
 *  - SCAN_SHIPMENT_PIECE_UPDATE_API_DOCUMENTATION.md
 *  - SCAN_RESOLVE_API_DOCUMENTATION.md
 *  - CUSTOMER_SHIPMENT_PIECES_API_DOCUMENTATION.md
 *  - FRONTEND_PER_PIECE_WEIGHT_PRICING_GUIDE.md  (weight + dim + pricing rollup)
 *
 * Every scan endpoint accepts an optional `client_event_id` (UUID v4).
 * Re-posting the same value returns the prior result with
 * `idempotent_replay: true` — no duplicate updates, no duplicate WhatsApp.
 *
 * Writable piece fields (per the per-piece weight/dim/pricing rollup release):
 *   weight_kg, width_cm, length_cm, height_cm, volumetric_weight_kg (optional;
 *   auto-computed by the server when blank from W*L*H/DIM_DIVISOR_AIR),
 *   product_category_code, meter_price_override, extra_cost, plus the existing
 *   status / event_type / location / note / description fields.
 *
 * Read-only fields returned by the server:
 *   chargeable_weight_kg (max(weight_kg, volumetric_weight_kg)),
 *   price (per-piece rated subtotal),
 *   sequence, barcode, human_label.
 */

/**
 * Local volumetric-weight preview helper. The authoritative number comes back
 * in the server response — this is only a hint so the form can render an
 * instant estimate while the user is still typing.
 *
 * @param {number|string} width
 * @param {number|string} length
 * @param {number|string} height
 * @param {number} divisor cm³ / kg, defaults to IATA 5000.
 * @returns {number|null} kilograms, or null when any input is missing/zero.
 */
export function previewVolumetricWeightKg(width, length, height, divisor = 5000) {
  const w = Number(width);
  const l = Number(length);
  const h = Number(height);
  const d = Number(divisor);
  if (!Number.isFinite(w) || !Number.isFinite(l) || !Number.isFinite(h) || !Number.isFinite(d)) return null;
  if (w <= 0 || l <= 0 || h <= 0 || d <= 0) return null;
  return (w * l * h) / d;
}

/**
 * Compute the chargeable weight in kg (max of actual and volumetric).
 * Mirrors `ShipmentPiece.save()` server-side computation.
 *
 * @param {number|string|null} weightKg
 * @param {number|string|null} volumetricKg
 * @returns {number|null}
 */
export function previewChargeableWeightKg(weightKg, volumetricKg) {
  const w = weightKg == null || weightKg === '' ? null : Number(weightKg);
  const v = volumetricKg == null || volumetricKg === '' ? null : Number(volumetricKg);
  const candidates = [];
  if (Number.isFinite(w) && w > 0) candidates.push(w);
  if (Number.isFinite(v) && v > 0) candidates.push(v);
  if (!candidates.length) return null;
  return Math.max(...candidates);
}

/**
 * Extract per-field validation errors from a DRF 400 response for a
 * shipment piece create / patch call. The doc lists these structured keys:
 *
 *   { dimensions: ["…"], weight_kg: ["…"], width_cm: ["…"],
 *     length_cm: ["…"], height_cm: ["…"], sequence: ["…"] }
 *
 * @param {object} axiosError
 * @returns {object} flat object of fieldName -> firstMessage string
 */
export function extractShipmentPieceFieldErrors(axiosError) {
  const out = {};
  const data = axiosError && axiosError.response && axiosError.response.data;
  if (!data || typeof data !== 'object') return out;
  const keys = [
    'dimensions',
    'weight_kg',
    'width_cm',
    'length_cm',
    'height_cm',
    'volumetric_weight_kg',
    'sequence',
    'meter_price_override',
    'product_category_code',
    'extra_cost',
  ];
  keys.forEach((k) => {
    const v = data[k];
    if (Array.isArray(v) && v.length) {
      out[k] = String(v[0]);
    } else if (typeof v === 'string' && v.trim()) {
      out[k] = v.trim();
    }
  });
  return out;
}

/**
 * Extract per-row validation errors from a nested `pieces[]` array on
 * POST /api/shipment/ (create shipment with pieces).
 *
 * @param {object} axiosError
 * @returns {object} index -> fieldErrors map (same shape as extractShipmentPieceFieldErrors)
 */
export function extractNestedShipmentPieceErrors(axiosError) {
  const out = {};
  const data = axiosError && axiosError.response && axiosError.response.data;
  if (!data || typeof data !== 'object') return out;
  const pieces = data.pieces;
  if (Array.isArray(pieces)) {
    pieces.forEach((pieceErr, index) => {
      if (pieceErr == null) return;
      if (typeof pieceErr === 'string' && pieceErr.trim()) {
        out[index] = { _root: pieceErr.trim() };
        return;
      }
      if (typeof pieceErr === 'object') {
        const rowErrors = {};
        Object.entries(pieceErr).forEach(([k, v]) => {
          if (Array.isArray(v) && v.length) rowErrors[k] = String(v[0]);
          else if (typeof v === 'string' && v.trim()) rowErrors[k] = v.trim();
        });
        if (Object.keys(rowErrors).length) out[index] = rowErrors;
      }
    });
    return out;
  }
  if (typeof pieces === 'string' && pieces.trim()) {
    out._form = pieces.trim();
  } else if (Array.isArray(pieces) && pieces.length && typeof pieces[0] === 'string') {
    out._form = pieces[0];
  }
  return out;
}

const pieceBaseURL = `${apiURL}/api/shipment_piece`;
const pieceEventBaseURL = `${apiURL}/api/shipment_piece_tracking_event`;

function authHeaders() {
  axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
}

function appendParams(searchParams, params) {
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
}

/**
 * Best-effort UUID v4 generator. Prefers `crypto.randomUUID()` when available,
 * falls back to a v4 polyfill compatible with older browsers / iOS WKWebView.
 */
export function newClientEventId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) { /* ignore and fall back */ }
  const rnd = (n) => {
    const arr = new Uint8Array(n);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      crypto.getRandomValues(arr);
    } else {
      for (let i = 0; i < n; i += 1) arr[i] = Math.floor(Math.random() * 256);
    }
    return arr;
  };
  const b = rnd(16);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ---------------------------------------------------------------------------
// Shipment piece CRUD
// ---------------------------------------------------------------------------

/**
 * List shipment pieces.
 * Permission: shipment_pieces_list
 * Filters: shipment, status, event_type, location, barcode, barcode_contains, deleted
 */
export async function listShipmentPieces(params = {}) {
  authHeaders();
  const searchParams = new URLSearchParams();
  appendParams(searchParams, params);
  const qs = searchParams.toString();
  return axios.get(`${pieceBaseURL}/${qs ? `?${qs}` : ''}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** GET /api/shipment_piece/<id>/ — Permission: shipment_pieces_view */
export async function getShipmentPiece(id) {
  authHeaders();
  return axios.get(`${pieceBaseURL}/${id}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** POST /api/shipment_piece/ — Permission: shipment_pieces_create */
export async function createShipmentPiece(data) {
  authHeaders();
  return axios.post(`${pieceBaseURL}/`, data, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** PUT /api/shipment_piece/<id>/ — Permission: shipment_pieces_update */
export async function updateShipmentPiece(id, data) {
  authHeaders();
  return axios.put(`${pieceBaseURL}/${id}/`, data, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** PATCH /api/shipment_piece/<id>/ — Permission: shipment_pieces_update */
export async function patchShipmentPiece(id, data) {
  authHeaders();
  return axios.patch(`${pieceBaseURL}/${id}/`, data, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** DELETE /api/shipment_piece/<id>/ — Permission: shipment_pieces_delete */
export async function deleteShipmentPiece(id) {
  authHeaders();
  return axios.delete(`${pieceBaseURL}/${id}/`, { withCredentials: true });
}

/** POST /api/shipment_piece/recover/ — Permission: shipment_pieces_recover */
export async function recoverShipmentPieces(ids) {
  authHeaders();
  return axios.post(
    `${pieceBaseURL}/recover/`,
    { ids },
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    }
  );
}

// ---------------------------------------------------------------------------
// Scan flows
// ---------------------------------------------------------------------------

/**
 * PUT /api/shipment_piece/<id>/scan_update/
 * Single-piece scan. Body: { location?, status?, event_type?, send_whatsapp?, client_event_id? }.
 * Response: full piece + { whatsapp_sent, idempotent_replay }.
 * Permission: shipment_pieces_scan_update
 */
export async function scanUpdateShipmentPiece(pieceId, body = {}) {
  authHeaders();
  const payload = {
    ...body,
    client_event_id: body && body.client_event_id ? body.client_event_id : newClientEventId(),
  };
  return axios.put(`${pieceBaseURL}/${pieceId}/scan_update/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * POST /api/shipment_piece/scan_lookup/
 * Read-only barcode lookup. Body: { barcode }.
 * Possible responses:
 *  - 200: full piece object
 *  - 409 { error: "use_universal_resolve" } — caller should retry against scan/resolve/
 *  - 400 { error: "bad_barcode_format" | "bad_check_digit" }
 *  - 404 { error: "piece_not_found" }
 * Permission: shipment_pieces_scan_lookup
 */
export async function scanLookupShipmentPiece(barcode) {
  authHeaders();
  return axios.post(`${pieceBaseURL}/scan_lookup/`, { barcode }, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * POST /api/shipment_piece/bulk_scan_update/
 * Body: { barcodes[], location?, status?, event_type?, send_whatsapp?, client_event_ids? }.
 * `client_event_ids` is a map keyed by barcode. The helper fills any missing
 * entries with a fresh UUID so retries dedupe per (parent, cid).
 * Permission: shipment_pieces_bulk_scan_update
 */
export async function bulkScanUpdateShipmentPieces(body = {}) {
  authHeaders();
  const barcodes = Array.isArray(body.barcodes) ? body.barcodes : [];
  const provided = body.client_event_ids && typeof body.client_event_ids === 'object'
    ? body.client_event_ids
    : {};
  const client_event_ids = {};
  barcodes.forEach((bc) => {
    if (bc == null) return;
    const key = String(bc);
    client_event_ids[key] = provided[key] || newClientEventId();
  });
  const payload = { ...body, barcodes, client_event_ids };
  return axios.post(`${pieceBaseURL}/bulk_scan_update/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * PUT /api/shipment_unit/<id>/scan_update/
 * Scan-update a shipment unit. Same idempotency rules as the piece flow.
 * Permission: shipment_unit_scan_update
 */
export async function scanUpdateShipmentUnit(unitId, body = {}) {
  authHeaders();
  const payload = {
    ...body,
    client_event_id: body && body.client_event_id ? body.client_event_id : newClientEventId(),
  };
  return axios.put(`${apiURL}/api/shipment_unit/${unitId}/scan_update/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * POST /api/scan/resolve/
 * Universal scan resolver. Body: { barcode }.
 * Response: { kind: 'piece' | 'shipment' | 'unit', piece, shipment, unit }.
 * 404 means the code didn't match anything.
 * Permission: scan_resolve
 */
export async function scanResolve(barcode) {
  authHeaders();
  return axios.post(`${apiURL}/api/scan/resolve/`, { barcode }, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

// ---------------------------------------------------------------------------
// Per-piece tracking events
// ---------------------------------------------------------------------------

/**
 * GET /api/shipment_piece_tracking_event/?shipment_piece=<id>
 * Permission: shipment_piece_tracking_events_list
 */
export async function listShipmentPieceTrackingEvents(params = {}) {
  authHeaders();
  const searchParams = new URLSearchParams();
  appendParams(searchParams, params);
  const qs = searchParams.toString();
  return axios.get(`${pieceEventBaseURL}/${qs ? `?${qs}` : ''}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** GET /api/shipment_piece_tracking_event/<id>/ — Permission: shipment_piece_tracking_events_view */
export async function getShipmentPieceTrackingEvent(id) {
  authHeaders();
  return axios.get(`${pieceEventBaseURL}/${id}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** POST /api/shipment_piece_tracking_event/ — Permission: shipment_piece_tracking_events_create */
export async function createShipmentPieceTrackingEvent(data) {
  authHeaders();
  return axios.post(`${pieceEventBaseURL}/`, data, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * Update (full or partial) a shipment piece tracking event.
 * Permission: shipment_piece_tracking_events_update
 *
 * Include `timestamp` (ISO 8601 UTC) only when changing it — presence triggers latest-event
 * re-sync on the parent piece and parent shipment rollup; omit it to sync from the edited event.
 */
export async function updateShipmentPieceTrackingEvent(id, data, partial = true) {
  authHeaders();
  const payload = { ...data };
  if (payload.location && typeof payload.location === 'object' && payload.location.id != null) {
    payload.location = payload.location.id;
  }
  const method = partial ? 'patch' : 'put';
  return axios[method](`${pieceEventBaseURL}/${id}/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/** DELETE /api/shipment_piece_tracking_event/<id>/ — Permission: shipment_piece_tracking_events_delete */
export async function deleteShipmentPieceTrackingEvent(id) {
  authHeaders();
  return axios.delete(`${pieceEventBaseURL}/${id}/`, { withCredentials: true });
}

/**
 * Bulk update shipment piece tracking events.
 * Permission: shipment_piece_tracking_events_bulk_update
 * Body: array of { id, ...fields }. Each item is a partial patch.
 */
export async function bulkUpdateShipmentPieceTrackingEvents(items) {
  authHeaders();
  const payload = items.map((item) => {
    const { id, ...rest } = item;
    const out = { id, ...rest };
    if (out.location && typeof out.location === 'object' && out.location.id != null) out.location = out.location.id;
    if (out.shipment_piece && typeof out.shipment_piece === 'object' && out.shipment_piece.id != null) {
      out.shipment_piece = out.shipment_piece.id;
    }
    return out;
  });
  return axios.put(`${pieceEventBaseURL}/bulk-update/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * Bulk soft-delete shipment piece tracking events.
 * Permission: shipment_piece_tracking_events_bulk_delete
 * Body: array of IDs
 */
export async function bulkDeleteShipmentPieceTrackingEvents(ids) {
  authHeaders();
  return axios.delete(`${pieceEventBaseURL}/bulk-delete/`, {
    data: ids,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

// ---------------------------------------------------------------------------
// Customer / drill-down endpoints
// ---------------------------------------------------------------------------

/**
 * GET /api/shipment/<shipmentId>/pieces/
 * Paginated drill-down for shipments with many pieces.
 * Filters: status, event_type, location, barcode, page, page_size.
 * Permission: shipment_pieces_customer_list_for_shipment (customer) or shipment_pieces_list (staff).
 */
export async function getShipmentPiecesForShipment(shipmentId, params = {}) {
  authHeaders();
  const searchParams = new URLSearchParams();
  appendParams(searchParams, params);
  const qs = searchParams.toString();
  return axios.get(`${apiURL}/api/shipment/${shipmentId}/pieces/${qs ? `?${qs}` : ''}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
}

/**
 * GET /api/track/piece/<barcode>/
 * Public (no auth) tracking page for a piece barcode. Safe to share.
 */
export async function getPublicPieceTrack(barcode) {
  return axios.get(`${apiURL}/api/track/piece/${encodeURIComponent(barcode)}/`, {
    headers: { 'Content-Type': 'application/json' },
  });
}

// ---------------------------------------------------------------------------
// Labels (4×6 in thermal PDF)
// ---------------------------------------------------------------------------

/**
 * GET /api/shipment_piece/<id>/label/
 * Returns a 4x6 in label PDF. Caller should treat `response.data` as a Blob.
 * Permission: shipment_pieces_label
 */
export async function getShipmentPieceLabelPdf(pieceId) {
  authHeaders();
  return axios.get(`${pieceBaseURL}/${pieceId}/label/`, {
    responseType: 'blob',
    withCredentials: true,
  });
}

/**
 * Convenience: build a URL that opens the single-piece label in a new tab
 * (the request still needs the JWT, so this is best used with same-origin
 * cookies / a small server proxy). For most flows prefer
 * `downloadShipmentPieceLabelPdf` which streams the blob via the JWT helper.
 */
export function getShipmentPieceLabelUrl(pieceId) {
  return `${apiURL}/api/shipment_piece/${pieceId}/label/`;
}

/**
 * POST /api/shipment_piece/labels/batch/
 * Body: { piece_ids: number[] }. Returns one PDF, one piece per page.
 * Permission: shipment_pieces_label_batch
 */
export async function batchShipmentPieceLabelsPdf(pieceIds) {
  authHeaders();
  return axios.post(`${pieceBaseURL}/labels/batch/`, { piece_ids: pieceIds }, {
    responseType: 'blob',
    withCredentials: true,
  });
}

/**
 * Trigger a browser download / print for a Blob-based label PDF.
 * @param {Blob} blob
 * @param {string} filename
 */
export function openPdfBlob(blob, filename = 'label.pdf') {
  try {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.warn('openPdfBlob error', e);
  }
}

/**
 * Download and open the single-piece label PDF. Returns the Blob for callers
 * that want to embed it instead of opening directly.
 */
export async function downloadShipmentPieceLabelPdf(pieceId, filename) {
  const res = await getShipmentPieceLabelPdf(pieceId);
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
  openPdfBlob(blob, filename || `piece-${pieceId}-label.pdf`);
  return blob;
}

/** Download and open the batch labels PDF. */
export async function downloadBatchShipmentPieceLabelsPdf(pieceIds, filename) {
  const res = await batchShipmentPieceLabelsPdf(pieceIds);
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
  openPdfBlob(blob, filename || `piece-labels-${(pieceIds || []).join('-')}.pdf`);
  return blob;
}
